import type {
  DropDownRef,
  JsonData,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
  OdinDataSender,
  OdinKey,
} from '@myosh/odin-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import type { LookupType } from '../../../@types/forms';
import type { FieldOptionsApiProps } from '../../../@types/options';
import useLookupTypeField from '../../../hooks/use-lookup-type-field';
import { useLazyFieldOptionsQuery } from '../../../redux/services/field';
import { isWebView } from '../../../redux/util/webview-base-query';
import { forceAssert } from '../../../utils/type-guards';
import {
  filterByParentFieldOptionId,
  normalizeFieldFilters,
  parseDependsOnValue,
} from '../../../utils/webview-field-utils';

interface FetchAndSetDataOptions {
  fieldOptions: FieldOptionsApiProps;
  localDependsOnFilter?: number;
  requestId?: string;
  suppressNextCursor?: boolean;
}

/**
 * A hook that handles all aspects for fetching the person fields data.
 *
 * @param formId the form id where the field is used
 * @param control the hook form 'control' object
 * @param lookupType the defined lookup type, currently only `WITH_FIELD_KEY` and `WITH_HIERARCHY_FIELD` are supported
 * @param lookupKeyId the 'id' of the form field from which the value should be read.
 * @param lookupHierarchyId the 'id' of the hierarchy from which the value should be read.
 * @param dependsOn an optional query string parameter
 *
 */
const useComboboxFieldOptions = (
  formId: number,
  control?: Control,
  lookupType?: LookupType,
  lookupKeyId?: number,
  lookupHierarchyId?: number,
  dependsOn?: OdinKey
) => {
  const [options, setOptions] = useState<OdinDataRetrievalOptions>();
  const subscriberRef = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const dropDownRef = useRef<DropDownRef>(null);
  const dataRequestedRef = useRef<boolean | undefined>(undefined);
  const hierarchyFieldIdRef = useRef<number | undefined>(undefined);

  const [getFieldOptions] = useLazyFieldOptionsQuery();

  const {
    shouldUseLookup,
    hierarchyFieldId: resolvedHierarchyFieldId,
    keyValue,
  } = useLookupTypeField(control, lookupType, lookupKeyId, lookupHierarchyId);

  useEffect(() => {
    if (shouldUseLookup) {
      hierarchyFieldIdRef.current = resolvedHierarchyFieldId;
    }
  }, [shouldUseLookup, resolvedHierarchyFieldId]);

  useEffect(() => {
    if (options) {
      const inWebView = isWebView();
      const _options = {
        queryId: Number(options.fieldId),
        formId,
        filters: inWebView ? normalizeFieldFilters(options.fieldFilters) : options.fieldFilters,
        dependsOn: inWebView ? undefined : dependsOn,
        next: inWebView ? undefined : (options.customProperties?.next as string),
        limit: inWebView ? -1 : options.pageSize || 50,
      };

      // In WebView mode, parse dependsOn for local parentFieldOptionId filtering
      const localDependsOnFilter = inWebView ? parseDependsOnValue(dependsOn) : undefined;

      const suppressNextCursor = inWebView;

      if (shouldUseLookup) {
        if (hierarchyFieldIdRef.current || keyValue) {
          fetchAndSetData({
            fieldOptions: {
              ..._options,
              keyValue,
              hierarchyFieldId: hierarchyFieldIdRef.current,
            },
            localDependsOnFilter,
            requestId: options.requestId,
            suppressNextCursor,
          });
        } else {
          subscriberRef.current?.sendData();
          dataRequestedRef.current = false;
        }
      } else {
        fetchAndSetData({
          fieldOptions: _options,
          localDependsOnFilter,
          requestId: options.requestId,
          suppressNextCursor,
        });
      }
    }
  }, [options, shouldUseLookup, keyValue, dependsOn, formId]);

  const fetchAndSetData = useCallback(
    ({ fieldOptions, localDependsOnFilter, requestId, suppressNextCursor }: FetchAndSetDataOptions) => {
      getFieldOptions(fieldOptions, true)
        .unwrap()
        .then((result) => {
          let data = result.items ?? [];
          if (localDependsOnFilter !== undefined) {
            data = filterByParentFieldOptionId(data, localDependsOnFilter);
          }
          subscriberRef.current?.sendData({
            data: forceAssert<JsonData>(data),
            requestId,
            customProperties: { next: suppressNextCursor ? undefined : result.next },
          });
        })
        .catch(() => subscriberRef.current?.sendData())
        .finally(() => (dataRequestedRef.current = false));
    },
    [getFieldOptions]
  );

  const onSelectorVisible = (open: boolean) => {
    if (shouldUseLookup || dependsOn !== undefined) {
      if (!open) {
        dropDownRef.current?.resetDropDownData();
      } else if (open && dataRequestedRef.current === false) {
        dropDownRef.current?.getDataOverwritePageCache();
      }
    }
  };

  return {
    setOptions,
    onSelectorVisible,
    subscriberRef,
    dataRequestedRef,
    dropDownRef,
  };
};

export default useComboboxFieldOptions;
